import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  programApi,
  type CreateProgramRequest,
  type UpdateProgramRequest,
  type GenerateProgramRequest
} from '../api/danceApi';

export const programKeys = {
  all: ['programs'] as const,
  lists: () => [...programKeys.all, 'list'] as const,
  list: () => [...programKeys.lists()] as const,
  details: () => [...programKeys.all, 'detail'] as const,
  detail: (id: string) => [...programKeys.details(), id] as const,
  public: (publicId: string) => [...programKeys.all, 'public', publicId] as const,
};

export function usePrograms(enabled = true) {
  return useQuery({
    queryKey: programKeys.list(),
    queryFn: () => programApi.listPrograms(),
    staleTime: 60000,
    enabled,
  });
}

export function useProgram(programId: string | undefined) {
  return useQuery({
    queryKey: programKeys.detail(programId || ''),
    queryFn: () => programApi.getProgram(programId!),
    enabled: !!programId,
    staleTime: 300000,
  });
}

export function usePublicProgram(publicId: string | undefined) {
  return useQuery({
    queryKey: programKeys.public(publicId || ''),
    queryFn: () => programApi.getPublicProgram(publicId!),
    enabled: !!publicId,
    staleTime: 300000,
  });
}

export function useCreateProgram() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProgramRequest) => programApi.createProgram(data),
    onSuccess: (program) => {
      queryClient.setQueryData(programKeys.detail(program.programId), program);
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
    }
  });
}

export function useUpdateProgram() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ programId, data }: { programId: string; data: UpdateProgramRequest }) =>
      programApi.updateProgram(programId, data),
    onSuccess: (program) => {
      queryClient.setQueryData(programKeys.detail(program.programId), program);
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
    }
  });
}

export function useDeleteProgram() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (programId: string) => programApi.deleteProgram(programId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
    }
  });
}

export function useGenerateProgram() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GenerateProgramRequest) => programApi.generateProgram(data),
    onSuccess: (program) => {
      queryClient.setQueryData(programKeys.detail(program.programId), program);
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
    }
  });
}
